import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import Logo from '@/components/Logo';
import {
  useCustomerEmails,
  useEnregistrerEntete,
  useEnvoyerCourriel,
  useReouvrirCommande,
  useSoumettre,
  useSupprimerCommande,
  urlPdf,
  type Order,
} from '@/lib/orders';
import { useSession } from '@/lib/session';
import { formatMoney } from '@/lib/utils';

/**
 * Plan §5.5 — récapitulatif, PO client, notes, puis soumission.
 * Une fois soumise, la même vue devient l'écran de confirmation portant le
 * numéro de commande : le rep peut le lire au client sans changer d'écran.
 */
export default function Confirmation({ order, onRetour }: { order: Order; onRetour: () => void }) {
  const [signerName, setSignerName] = useState(order.signerName ?? '');
  const [poNumber, setPoNumber] = useState(order.poNumber ?? '');
  const [notes, setNotes] = useState(order.notes ?? '');
  const entete = useEnregistrerEntete(order.id);
  const soumettre = useSoumettre(order.id);
  const reouvrir = useReouvrirCommande(order.id);
  const supprimer = useSupprimerCommande(order.id);
  const navigate = useNavigate();
  const { session } = useSession();
  const estAdmin = session?.user.role === 'ADMIN';

  const soumise = order.status === 'SUBMITTED';
  const annulee = order.status === 'CANCELLED';

  function onSupprimer() {
    if (!confirm('Supprimer DÉFINITIVEMENT cette commande ? Cette action est irréversible.')) return;
    supprimer.mutate(undefined, { onSuccess: () => void navigate('/admin/orders') });
  }

  async function envoyer() {
    // L'entête est enregistrée AVANT la soumission : après, la commande est
    // figée et n'accepte plus de modification.
    await entete.mutateAsync({
      poNumber: poNumber.trim() || null,
      signerName: signerName.trim() || null,
      notes: notes.trim() || null,
    });
    await soumettre.mutateAsync();
  }

  return (
    <div className="mx-auto min-h-full max-w-4xl space-y-6 p-6">
      <header className="flex items-center gap-3">
        <Logo className="h-10" />
        <h1 className="text-2xl font-bold">
          {soumise ? 'Commande soumise' : annulee ? 'Commande annulée' : 'Réviser la commande'}
        </h1>
      </header>

      {soumise && (
        <div className="rounded-xl border border-green-600/40 bg-green-600/10 p-6 text-center">
          <p className="text-sm font-medium text-green-800 dark:text-green-300">
            Commande enregistrée
          </p>
          {/* Le numéro en très gros : c'est ce que le rep lit au client. */}
          <p className="mt-1 font-mono text-4xl font-bold tracking-tight">{order.number}</p>
        </div>
      )}

      {annulee && (
        <div className="border-destructive/40 bg-destructive/10 rounded-xl border p-6 text-center">
          <p className="text-destructive text-sm font-medium">Cette commande a été annulée</p>
          {order.number && (
            <p className="text-muted-foreground mt-1 font-mono text-2xl font-bold">{order.number}</p>
          )}
        </div>
      )}

      <section className="bg-card rounded-xl border p-5">
        <h2 className="text-muted-foreground text-sm font-medium">Client</h2>
        <p className="text-lg font-semibold">{order.customer.name}</p>
        <p className="text-muted-foreground font-mono text-sm">{order.customer.custNum}</p>
        {order.customer.city && (
          <p className="text-muted-foreground text-sm">
            {[order.customer.address, order.customer.city, order.customer.province]
              .filter(Boolean)
              .join(', ')}
          </p>
        )}
      </section>

      <section className="bg-card overflow-hidden rounded-xl border">
        <table className="w-full text-sm">
          <thead className="bg-muted">
            <tr className="text-left">
              <th className="p-3 font-medium">Pièce</th>
              <th className="p-3 font-medium">Description</th>
              <th className="p-3 text-right font-medium">Qté</th>
              <th className="p-3 text-right font-medium">Prix</th>
              <th className="p-3 text-right font-medium">Total</th>
            </tr>
          </thead>
          <tbody>
            {order.lines.map((l) => (
              <tr key={l.id} className="border-t align-top">
                <td className="p-3 font-mono text-xs">
                  <span className="text-muted-foreground">{l.lineCode}</span> {l.partNumber}
                </td>
                <td className="p-3">
                  {l.description}
                  {l.promoLabel && (
                    <span className="block text-xs font-medium text-green-700 dark:text-green-400">
                      {l.promoLabel} · −{formatMoney(l.discountAmount)}
                    </span>
                  )}
                </td>
                <td className="p-3 text-right tabular-nums">{l.qty}</td>
                <td className="p-3 text-right tabular-nums">{formatMoney(l.unitPrice)}</td>
                <td className="p-3 text-right font-medium tabular-nums">{formatMoney(l.lineTotal)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <section className="bg-card space-y-2 rounded-xl border p-5">
        <div className="flex justify-between text-sm">
          <span className="text-muted-foreground">Sous-total</span>
          <span className="tabular-nums">{formatMoney(order.subtotal)}</span>
        </div>
        {order.discountTotal !== '0.00' && (
          <div className="flex justify-between text-sm text-green-700 dark:text-green-400">
            <span>Rabais</span>
            <span className="tabular-nums">−{formatMoney(order.discountTotal)}</span>
          </div>
        )}
        <div className="flex items-baseline justify-between border-t pt-2">
          <span className="font-semibold">Total avant taxes</span>
          <span className="text-3xl font-bold tabular-nums">{formatMoney(order.total)}</span>
        </div>
      </section>

      {annulee ? (
        <>
          {(reouvrir.isError || supprimer.isError) && (
            <p className="text-destructive font-medium">
              {((reouvrir.error ?? supprimer.error) as Error)?.message}
            </p>
          )}
          <div className="flex flex-wrap gap-3">
            {estAdmin ? (
              <>
                <button
                  onClick={() => reouvrir.mutate()}
                  disabled={reouvrir.isPending}
                  className="border-input h-16 flex-1 rounded-lg border px-6 text-lg font-medium disabled:opacity-40"
                >
                  {reouvrir.isPending ? 'Réouverture…' : 'Réouvrir en brouillon'}
                </button>
                <button
                  onClick={onSupprimer}
                  disabled={supprimer.isPending}
                  className="border-destructive/40 text-destructive h-16 rounded-lg border px-6 text-lg font-medium disabled:opacity-40"
                >
                  {supprimer.isPending ? 'Suppression…' : 'Supprimer la commande'}
                </button>
              </>
            ) : (
              <Link
                to="/"
                className="border-input flex h-16 items-center rounded-lg border px-6 text-lg font-medium"
              >
                Accueil
              </Link>
            )}
          </div>
        </>
      ) : !soumise ? (
        <>
          <section className="bg-card space-y-4 rounded-xl border p-5">
            <label className="block space-y-1.5">
              <span className="text-sm font-medium">
                Nom et prénom <span className="text-destructive">*</span>
              </span>
              <input
                value={signerName}
                onChange={(e) => setSignerName(e.target.value)}
                maxLength={191}
                placeholder="Personne qui passe la commande"
                autoCapitalize="words"
                className="border-input bg-background h-14 w-full rounded-lg border px-3 text-base"
              />
            </label>
            <label className="block space-y-1.5">
              <span className="text-sm font-medium">Bon de commande du client (facultatif)</span>
              <input
                value={poNumber}
                onChange={(e) => setPoNumber(e.target.value)}
                maxLength={64}
                className="border-input bg-background h-14 w-full rounded-lg border px-3 text-base"
              />
            </label>
            <label className="block space-y-1.5">
              <span className="text-sm font-medium">Notes (facultatif)</span>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
                maxLength={2000}
                className="border-input bg-background w-full rounded-lg border p-3 text-base"
              />
            </label>
          </section>

          {(soumettre.isError || entete.isError) && (
            <p className="text-destructive font-medium">
              {((soumettre.error ?? entete.error) as Error).message}
            </p>
          )}

          <div className="flex flex-wrap gap-3">
            <button
              onClick={onRetour}
              className="border-input h-16 rounded-lg border px-6 text-lg font-medium"
            >
              ← Continuer les achats
            </button>
            <button
              onClick={() => void envoyer()}
              disabled={
                soumettre.isPending ||
                entete.isPending ||
                order.lines.length === 0 ||
                signerName.trim() === ''
              }
              className="bg-primary text-primary-foreground h-16 flex-1 rounded-lg px-8 text-lg font-semibold disabled:opacity-40"
            >
              {soumettre.isPending
                ? 'Envoi…'
                : signerName.trim() === ''
                  ? 'Nom et prénom requis'
                  : 'Soumettre la commande'}
            </button>
          </div>
        </>
      ) : (
        <>
          <EnvoiClient order={order} />

          <div className="flex flex-wrap gap-3">
            <Link
              to="/order/new"
              className="bg-primary text-primary-foreground flex h-16 flex-1 items-center justify-center rounded-lg px-8 text-lg font-semibold"
            >
              Nouvelle commande
            </Link>
            <Link
              to="/"
              className="border-input flex h-16 items-center rounded-lg border px-6 text-lg font-medium"
            >
              Accueil
            </Link>
          </div>
        </>
      )}
    </div>
  );
}

// Option spéciale du menu déroulant : saisir une adresse absente de la liste.
const AUTRE = '__autre__';

/** Envoi de la confirmation au client : PDF + courriel (écran d'une commande soumise). */
function EnvoiClient({ order }: { order: Order }) {
  const { data } = useCustomerEmails(order.customer.id);
  const liste = data?.emails ?? [];
  const courriel = useEnvoyerCourriel(order.id);

  // `choix` = valeur du menu (un courriel enregistré, ou AUTRE pour saisie libre).
  const [choix, setChoix] = useState('');
  const [autreEmail, setAutreEmail] = useState('');

  // Choix par défaut à l'arrivée de la liste : le courriel de la fiche s'il y figure,
  // sinon le premier. Aucun courriel enregistré → saisie libre directe.
  // On attend que la requête soit RÉSOLUE (`data` défini) : sinon `liste` est encore
  // vide pendant le chargement et on figerait à tort le choix sur « Autre adresse ».
  useEffect(() => {
    if (!data) return;
    if (choix !== '') return;
    if (liste.length === 0) {
      setChoix(AUTRE);
      return;
    }
    const prefere =
      order.customer.email && liste.includes(order.customer.email)
        ? order.customer.email
        : liste[0];
    setChoix(prefere ?? AUTRE);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const saisieLibre = choix === AUTRE;
  const email = (saisieLibre ? autreEmail : choix).trim();
  const emailValide = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  return (
    <section className="bg-card space-y-4 rounded-xl border p-5">
      <h2 className="font-semibold">Confirmation au client</h2>

      <div className="flex flex-wrap items-end gap-3">
        <label className="flex-1 space-y-1.5">
          <span className="text-sm font-medium">Courriel du client</span>
          {liste.length > 0 ? (
            // Courriels enregistrés + une option pour saisir une AUTRE adresse.
            <select
              value={choix}
              onChange={(e) => setChoix(e.target.value)}
              className="border-input bg-background h-14 w-full min-w-56 rounded-lg border px-3 text-base"
            >
              {liste.map((e) => (
                <option key={e} value={e}>
                  {e}
                </option>
              ))}
              <option value={AUTRE}>✏️ Autre adresse…</option>
            </select>
          ) : (
            // Aucune adresse enregistrée : saisie libre directe.
            <input
              type="email"
              value={autreEmail}
              onChange={(e) => setAutreEmail(e.target.value)}
              placeholder="client@exemple.com"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              className="border-input bg-background h-14 w-full min-w-56 rounded-lg border px-3 text-base"
            />
          )}
        </label>
        <button
          onClick={() => courriel.mutate(email)}
          disabled={!emailValide || courriel.isPending}
          className="bg-primary text-primary-foreground h-14 rounded-lg px-6 font-semibold disabled:opacity-40"
        >
          {courriel.isPending ? 'Envoi…' : 'Envoyer par courriel'}
        </button>
      </div>

      {/* Champ libre révélé quand « Autre adresse » est choisi dans le menu. */}
      {liste.length > 0 && saisieLibre && (
        <input
          type="email"
          value={autreEmail}
          onChange={(e) => setAutreEmail(e.target.value)}
          placeholder="Saisir une autre adresse : client@exemple.com"
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
          autoFocus
          className="border-input bg-background h-14 w-full rounded-lg border px-3 text-base"
        />
      )}

      {courriel.isSuccess && (
        <p className="text-sm font-medium text-green-700 dark:text-green-400">
          Commande envoyée à {courriel.data.to}.
        </p>
      )}
      {courriel.isError && (
        <p className="text-destructive text-sm font-medium">{(courriel.error as Error).message}</p>
      )}

      {/* Le PDF s'ouvre dans un nouvel onglet (téléchargeable / imprimable). */}
      <a
        href={urlPdf(order.id)}
        target="_blank"
        rel="noopener noreferrer"
        className="text-primary inline-flex text-sm font-medium underline underline-offset-4"
      >
        Ouvrir le PDF de la commande ↗
      </a>
    </section>
  );
}
